import {
	FLAG_ENABLE_WEB,
	FLAG_REVEAL_MAP,
	GAME_TICKER_ENABLED,
	IS_DEV,
	IS_QA,
} from "@/config/constants";
import { catchUpEventEnd, useEventStore, type EventStoreState } from "@/event";
import {
	shouldStripEventResourcesOnLoad,
	zeroEventiumInResourcePatch,
} from "@/event/session/eventEndCleanup";
import {
	appliedEventGenerationForSave,
	emitEventPassPurchased as emitEventPassPurchasedTransport,
	eventMapCollectablesForSave,
	fetchEventOverview as fetchEventOverviewTransport,
	type EventSocketTransportDeps,
} from "@/event/transport";
import type { FrameName } from "@/game/entities/config";
import { tilerepoRemovableEventBuildingsFrameNames } from "@/game/entities/config";
import type { ResourceType } from "@/game/entities/config/resources";
import { eventUpgrades } from "@/game/entities/config/upgrades/event.ts";
import TileRepository from "@/game/TileRepository.ts";
import useMapActions, { type MapMutationAction } from "@/hooks/useMapActions";
import type { AchievementsStoreState } from "@/store/useAchievementsStore";
import useAchievementsStore from "@/store/useAchievementsStore";
import useBuildingsStorageStore, {
	type BuildingsStorageStoreState,
} from "@/store/useBuildingsStorageStore";
import type { ChestStoreState } from "@/store/useChestStore";
import useChestStore from "@/store/useChestStore";
import useMapExpansionStore, {
	type MapExpansionStoreState,
} from "@/store/useMapExpansionStore";
import useNewsStore from "@/store/useNewsStore";
import useOfferStore, { type OfferStoreState } from "@/store/useOfferStore";
import useQuestStore, { type QuestsStoreState } from "@/store/useQuestStore";
import type useQuickToolStore from "@/store/useQuickToolStore";
import { type QuickToolStoreState } from "@/store/useQuickToolStore";
import useResourcesStore, {
	type ResourcesStoreState,
} from "@/store/useResourcesStore";
import useRewardStore from "@/store/useRewardStore";
import useRuntimeStore from "@/store/useRuntimeStore";
import useSettingsStore, {
	type SettingsStoreState,
} from "@/store/useSettingsStore";
import useSettlementsStore, {
	type SettlementsStoreState,
} from "@/store/useSettlementsStore";
import useShopStore from "@/store/useShopStore";
import useSiegeSystemStore, {
	type SiegeSystemStoreState,
} from "@/store/useSiegeSystemStore";
import useUpgradesStore, {
	type UpgradesStoreState,
} from "@/store/useUpgradesStore";
import useUserStore, { type UserStoreState } from "@/store/useUserStore";
import type {
	EventGrandPrizeState,
	EventMilestonesState,
	EventOverviewResult,
	LeaderboardResult,
	UserData,
} from "@/types";
import { type Timestamp } from "@/types";
import type { OfferSlots, ShopItem } from "@/types/shop";
import type { EventStatus } from "@/types/ui";
import type { AllowedSkin } from "@/utils/shop/skins";
import { io, type Socket } from "socket.io-client";
import { flatten, parse, ValiError } from "valibot";
import Communicator from "./AppCommunicatior";
import GameError, { GameErrorType } from "./GameError";
import {
	AdAnalyticsSaveResultValidationSchema,
	ChangeNameResultValidationSchema,
	ClaimableRewardsLoadResponseValidationSchema,
	DeleteUserResultValidationSchema,
	GetScoreboardResultValidationSchema,
	GetUserByIdResultValidationSchema,
	InGameCompensationRewardsLoadResponseValidationSchema,
	LanguesListValidationSchema,
	LoadResponseValidationSchema,
	LocalTokenResponseValidationSchema,
	LocalUserIdResponseValidationSchema,
	OfferAnalyticsSaveResultValidationSchema,
	SaveResponseValidationSchema,
	StateResponseValidationSchema,
} from "./schemas";

enum SocketIoDisconnectReason {
	IO_SERVER_DISCONNECT = "io server disconnect",
	IO_CLIENT_DISCONNECT = "io client disconnect",
	PING_TIMEOUT = "ping timeout",
	TRANSPORT_CLOSE = "transport close",
	TRANSPORT_ERROR = "transport error",
}

const HTTP_API_URL = import.meta.env.VITE_HTTP_API_URL;
const WS_API_URL = import.meta.env.VITE_WS_API_URL;
const REQUEST_TIMEOUT = 8_000; // 8 seconds
const CONSOLE_COLOR = [
	"%c WS ",
	"background-color:teal;color:white;font-weight:700;",
];

// Toto su hodnoty zo zdrojakov socket.io klienta.
const SOCKET_IO_TIMEOUT_ERROR_STRING = "operation has timed out"; // Ak sa request nestihne spracovat do timeoutu.
const SOCKET_IO_DISCONNECT_ERROR_STRING = "socket has been disconnected"; // Toto nastava pri internom cisteni v libke po tom, ako nastane disconnect socketu.

type JwtToken = string;

type DeviceSecret = string;

type UserId = string;

type LocalUserIdResponse = {
	data: {
		registerOrLoginDevice: {
			accessToken: JwtToken; // z tohto requestu toto nepouzivame, dostaneme to z dalsieho request spolu s aj expiraciou
			deviceSecret: DeviceSecret;
			userId: UserId;
		};
	};
	errors?: Array<LocalTokenResponseError>;
};

type LocalTokenResponseError = {
	message: string;
};

type LocalTokenResponse = {
	data: {
		token: {
			token: JwtToken;
			expiresAt?: Timestamp;
		};
	};
	errors?: Array<LocalTokenResponseError>;
};

type StatusResponse = { status: boolean; data?: StatusData };

type StatusData = Record<string, unknown> & {
	event?: EventOverviewResult["event"];
};

type MapData = {
	tiles: Array<MapMutationAction>;
};

type MapExpansionStoreData = Partial<MapExpansionStoreState>;

type ResourcesStoreData = Partial<ResourcesStoreState>;

type BuildingsStorageStoreData = Partial<BuildingsStorageStoreState>;

type SettlementsStoreData = Partial<SettlementsStoreState>;

type QuestsStoreData = Partial<QuestsStoreState>;

type QuickToolStoreData = Partial<QuickToolStoreState>;

type OfferStoreData = Partial<OfferStoreState>;

type SettingsStoreData = Partial<SettingsStoreState>;

type UpgradesStoreData = Partial<UpgradesStoreState>;

type UserStoreData = Partial<UserStoreState>;

type ChestStoreData = Partial<ChestStoreState>;

type EventStoreData = Partial<EventStoreState>;

type ShopStoreData = ShopItem[];

type AchievementsStoreData = Partial<AchievementsStoreState>;

type SiegeSystemStoreData = Partial<SiegeSystemStoreState>;

type Language = { code: string; name: string; percent_completed: number };

type LanguesList = { languages: Language[] };

type TranslationsRefetchedData = {
	data: Record<string, string>;
	timestamp: number;
};

type TranslationsFetchRequest = {
	language: string;
	lastTimestamp?: number;
};

type TranslationsNotChangedData = {
	timestamp: number;
};

type FlashNews = {
	id: string;
	title: string;
	message: string;
	start_date: Date;
	end_date: Date;
	created_at: Date;
};

export interface FlashNewsData {
	status: boolean;
	flashNews: FlashNews[];
}

export type ClaimablePack = {
	name: string;
	store_id: string;
	attributes: {
		type: string;
		skin?: AllowedSkin;
	} & OfferSlots;
	promo_bonus_percent?: number; // NOTE: Increases the pack’s attribute value by the given percentile relative to the original 'value' amount.
};

export enum ClaimableRewardSource {
	PAID = "PAID",
	FREE = "FREE",
}

export type ClaimableRewardsData = {
	claimableRewards: Array<{
		_id: string;
		source?: ClaimableRewardSource;
		pack: ClaimablePack;
	}>;
};

export enum OfferAnalyticsType {
	OFFER_CLICK = "offer_click",
	OFFER_VIEW = "offer_view",
	OFFER_PAY_REQUEST = "offer_pay_request",
}

export enum OfferAnalyticsClickSource {
	LEFTBOX = "leftbox",
	STORE = "store",
	POPUP = "popup",
}

export enum AdAnalyticsType {
	INCOME_BOOST_AD = "income_boost_ad",
	OFFLINE_INCOME_AD = "offline_income_ad",
	CHEST_AD = "chest_ad",
	REWARDED_AD = "rewarded_ad",
}

export enum AdAnalyticsClickSource {
	LEFTBOX = "leftbox",
	STORE = "store",
	POPUP = "popup",
}

export type OfferAnalyticsData = {
	type: OfferAnalyticsType | "UNKNOWN";
	clickSource: OfferAnalyticsClickSource | "UNKNOWN";
	storeId: ShopItem["store_id"] | "UNKNOWN"; // ShopItem["store_id"] -> string
	offerName: ShopItem["name"] | "UNKNOWN"; // ShopItem["name"] -> string
};

export type AdAnalyticsData = {
	type: AdAnalyticsType | "UNKNOWN";
	clickSource: AdAnalyticsClickSource | "UNKNOWN";
	offerType?: string;
};

export type InGameRewardCurrency = {
	type: "currency";
	amount: number;
	currency: ResourceType;
};

export type InGameRewardBuilding = {
	type: "item";
	amount: number;
	frameName: FrameName;
};

export type InGameRewardSource = "manual";

export type InGameReward = {
	_id: string;
	user_id: string;
	rewards: InGameRewardCurrency | InGameRewardBuilding;
	source: InGameRewardSource;
	claimed: boolean;
};

export type InGameCompensationRewardsData = {
	inGameRewards: Array<InGameReward>;
};

export enum EmitStores {
	MAP = "map",
	MAP_EXPANSION = "mapExpansion",
	RESOURCES = "resources",
	BUILDINGS_STORAGE = "buildingsStorage",
	SETTLEMENTS = "settlements",
	QUESTS = "quests",
	QUICK_TOOL = "quickTool",
	OFFER = "offer",
	SETTINGS = "settings",
	UPGRADES = "upgrades",
	USER = "user",
	PACKS = "packs",
	FLASH_NEWS = "flashNews",
	CLAIMABLE_REWARDS = "claimableRewards",
	CHEST = "chest",
	// EVENT = "event",
	EVENT_OVERVIEW = "eventOverview",
	ACHIEVEMENTS = "achievements",
	EVENT_MAP_COLLECTABLES = "eventMapCollectables",
	SIEGE_SYSTEM = "siegeSystem",
}

const ALL_STORES = [
	EmitStores.MAP,
	EmitStores.MAP_EXPANSION,
	EmitStores.RESOURCES,
	EmitStores.BUILDINGS_STORAGE,
	EmitStores.SETTLEMENTS,
	EmitStores.QUESTS,
	EmitStores.QUICK_TOOL,
	EmitStores.OFFER,
	EmitStores.SETTINGS,
	EmitStores.UPGRADES,
	EmitStores.USER,
	EmitStores.PACKS,
	EmitStores.FLASH_NEWS,
	EmitStores.CLAIMABLE_REWARDS,
	EmitStores.CHEST,
	// EmitStores.EVENT,
	EmitStores.EVENT_OVERVIEW,
	EmitStores.ACHIEVEMENTS,
	EmitStores.EVENT_MAP_COLLECTABLES,
	EmitStores.SIEGE_SYSTEM,
] as const;

/** Core slices that must hydrate before event overview (level gates, teardown, etc.). */
const LOAD_STORE_KEYS_BEFORE_EVENT_OVERVIEW = [
	"mapExpansion",
	"settlements",
	"quests",
	"quickTool",
	"settings",
	"upgrades",
	"user",
	"packs",
	"flashNews",
	"chest",
	"offer",
	"achievements",
	"siegeSystem",
] as const;

type LoadRequestPayload = {
	stores?: readonly EmitStores[];
};

type SaveRequestPayload = LoadRequestPayload & {
	appliedEventGeneration?: number;
	claimedRewards?: Array<
		ClaimableRewardsData["claimableRewards"][number]["_id"]
	>;
	eventMilestones?: EventMilestonesState;
	eventGrandPrizeState?: EventGrandPrizeState;
};

type LoadResponse = {
	status: boolean;
	data: {
		map?: MapData;
		mapExpansion?: MapExpansionStoreData;
		resources?: ResourcesStoreData;
		buildingsStorage?: BuildingsStorageStoreData;
		settlements?: SettlementsStoreData;
		quests?: QuestsStoreData;
		quickTool?: QuickToolStoreData;
		offer?: OfferStoreData;
		settings?: SettingsStoreData;
		upgrades?: UpgradesStoreData;
		user?: UserStoreData & {
			last_tick_hash?: string;
		};
		packs?: ShopStoreData;
		flashNews?: FlashNewsData["flashNews"];
		claimableRewards?: ClaimableRewardsData["claimableRewards"];
		chest?: ChestStoreData;
		// event?: EventOverviewResult["event"];
		eventOverview?: EventStoreData;
		achievements?: AchievementsStoreData;
		siegeSystem?: SiegeSystemStoreData;
		maintenanceMode?: boolean;
	};
};

type SaveResponse = {
	status: boolean;
	data: {
		maintenanceMode: boolean;
		event: EventOverviewResult["event"];
		// after deploy is fully stable -> tighten to required
		eventStatus?: EventStatus;
		appliedEventGeneration?: number;
	};
};

class SocketService {
	private flutterToken: JwtToken | null = null;

	private socket: Socket | null = null;

	private isReconnecting: boolean = false;
	private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
	private isAppInBackground: boolean = false;
	private reconnectAttempts: number = 0;
	private maxReconnectAttempts: number = 5;
	private readonly handleOnline = (): void => {
		// skip if already connected — avoids tearing down a live socket
		if (this.isSocketActive) return;
		void this.forceReconnect();
	};

	// flush retry state — timer stored so it can be cancelled on disconnect
	public get isSocketActive(): boolean {
		return this.socket !== null && this.socket.connected;
	}

	public get hasFlutterToken(): boolean {
		if (this.flutterToken) return true;
		return new URLSearchParams(window.location.search).has("token");
	}

	private async getFlutterToken(): Promise<string | null> {
		if (this.flutterToken) return Promise.resolve(this.flutterToken);

		const searchParams = new URLSearchParams(window.location.search);
		this.flutterToken = searchParams.has("token")
			? searchParams.get("token")
			: null;

		return Promise.resolve(this.flutterToken);
	}

	private async getLocalUserId(): Promise<UserId | null> {
		try {
			const httpResponse = await fetch(`${HTTP_API_URL}/graphql`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					// Generovane z Postmana - nemenit!
					query: "mutation RegisterOrLoginDevice($input: RegisterOrLoginDeviceInput!) {\r\n  registerOrLoginDevice(loginDeviceInput: $input) {\r\n    accessToken\r\n    deviceSecret\r\n    userId\r\n  }\r\n}\r\n",
					variables: {
						input: {
							deviceUuid: "1234",
							deviceName: "iPhone 15 Pro",
							platform: "ios",
							country: "SK",
							lang: "sk",
							currency: "EUR",
							groupId: 1,
							appsFlyerId: "abc123xyz",
							acceptVop: 1717497600000,
							useForMarketingPurposes: true,
							deviceSecret:
								localStorage.getItem("deviceSecret") ||
								undefined, // undefined bude len pri registracii
						},
					},
				}),
				redirect: "follow",
			});
			if (!httpResponse.ok)
				throw new Error(
					`Wrong HTTP response status: ${httpResponse.status}`,
				);

			const response = (await httpResponse.json()) as LocalUserIdResponse;

			if (!response)
				throw new GameError(GameErrorType.BE_ERROR, "No data received");

			parse(LocalUserIdResponseValidationSchema, response);

			if (response.errors)
				throw new Error(
					response.errors
						.map(
							({
								message,
							}: LocalTokenResponseError): LocalTokenResponseError["message"] =>
								message,
						)
						.join(", "),
				);

			if (response.data.registerOrLoginDevice.deviceSecret)
				localStorage.setItem(
					"deviceSecret",
					response.data.registerOrLoginDevice.deviceSecret,
				);

			return response.data.registerOrLoginDevice.userId;
		} catch (error: unknown) {
			console.error("SocketService: Error getting local user ID:", error);
		}

		return null;
	}

	private async getLocalToken(): Promise<string | null> {
		const cookieMatch = document.cookie.match(
			/token_local=(?<token>[^;]+)/,
		);
		if (cookieMatch?.groups?.token)
			return Promise.resolve(cookieMatch.groups.token);

		try {
			const userId = await this.getLocalUserId();
			if (!userId) throw new Error("User ID not found");

			const httpResponse = await fetch(`${HTTP_API_URL}/graphql`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					// Generovane z Postmana - nemenit!
					query: "query($userId: String!) {\r\n    token(userId: $userId) {\r\n    token\r\n    expiresAt\r\n  }\r\n}",
					variables: { userId },
				}),
				redirect: "follow",
			});
			if (!httpResponse.ok)
				throw new Error(
					`Wrong HTTP response status: ${httpResponse.status}`,
				);

			const response = (await httpResponse.json()) as LocalTokenResponse;
			if (!response)
				throw new GameError(GameErrorType.BE_ERROR, "No data received");

			parse(LocalTokenResponseValidationSchema, response);

			if (response.errors)
				throw new Error(
					response.errors
						.map(
							({
								message,
							}: LocalTokenResponseError): LocalTokenResponseError["message"] =>
								message,
						)
						.join(", "),
				);

			if (response.data.token.expiresAt)
				document.cookie = `token_local=${response.data.token.token}; expires=${new Date(response.data.token.expiresAt * 1e3).toUTCString()}; path=/; secure; samesite=strict`;

			return response.data.token.token;
		} catch (error: unknown) {
			console.error("SocketService: Error getting local token:", error);
		}

		return null;
	}

	private async getToken(): Promise<string | null> {
		return IS_DEV || (IS_QA && FLAG_ENABLE_WEB)
			? this.getLocalToken()
			: this.getFlutterToken();
	}

	public setFlutterToken(flutterToken: string): void {
		this.flutterToken = flutterToken;
	}

	private handleError(
		context: string,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		error: Error | ValiError<any> | unknown,
		reject: CallableFunction,
	): void {
		if (!context || !error || !reject) return;

		if (error instanceof ValiError) {
			const { nested: nestedErrors } = flatten(error.issues);

			error.message += `\n\nValiError details:\n${JSON.stringify(nestedErrors)}`;

			console.error(`[${context}] ValiError details:`, nestedErrors);
		}

		if (error instanceof Error) {
			error.message = `[${context}] ${error.message}`;
		}

		reject(error);
	}

	private handleTimeoutError(message: string, error: Error | unknown): void {
		if (!message || !error) return;

		const errorMessage =
			error instanceof Error
				? error.message
				: (error?.toString() ?? "unknown error");

		// Namiesto "return" tu vyhodíme špecifickú chybu.
		// Toto okamžite preruší vykonávanie kódu a skočí do najbližšieho catch bloku.
		if (errorMessage === SOCKET_IO_DISCONNECT_ERROR_STRING) {
			throw new GameError(
				GameErrorType.NETWORK_DISCONNECT_DURING_REQUEST, // Tato je na FE schovana.
				"Socket disconnected during request",
			);
		}

		// Pôvodná logika pre skutočný timeout alebo iný error
		throw new GameError(
			errorMessage === SOCKET_IO_TIMEOUT_ERROR_STRING
				? GameErrorType.BE_TIMEOUT_ERROR // Tato je na FE schovana.
				: GameErrorType.BE_ERROR,
			`${message}: ${errorMessage}`,
		);
	}

	private eventTransportDeps(): EventSocketTransportDeps {
		return {
			handleTimeoutError: (message: string, error: unknown): void =>
				this.handleTimeoutError(message, error),
			requestTimeout: REQUEST_TIMEOUT,
		};
	}

	private dispatchInGameRewards(rewards: InGameReward[]): void {
		useRewardStore().setInGameCompensation(rewards);
	}

	public async connect(): Promise<void> {
		if (this.isSocketActive) return;

		const token = await this.getToken();
		if (!token) {
			console.error("Socket auth error occurred: token not found");
			Communicator.getInstance().relogUser();
			throw new GameError(
				GameErrorType.BE_ERROR,
				"Socket auth error occurred: token not found",
			);
		}

		// Set up visibility change listeners for iOS background handling
		this.setupVisibilityHandlers();

		return new Promise<void>(
			(
				resolve: CallableFunction,
				// reject: CallableFunction
			): void => {
				this.socket = io(WS_API_URL, {
					transports: ["websocket", "polling"],
					auth: { token },
					// options for better iOS compatibility
					forceNew: true,
					// built-in reconnect disabled — custom attemptReconnect in handleDisconnect owns this
					reconnection: false,
					reconnectionDelay: 1_000,
					reconnectionDelayMax: 5_000,
					reconnectionAttempts: this.maxReconnectAttempts,
					timeout: 20_000,
				});

				this.socket.on("connect", (): void => {
					// eslint-disable-next-line no-console
					console.log(...CONSOLE_COLOR, "socket connected");

					const wasReconnect = this.reconnectAttempts > 0;
					this.reconnectAttempts = 0;
					this.clearReconnectTimer();
					this.initExceptionHandler();
					this.initErrorHandler();
					this.initAuthErrorHandler();
					this.initReloadHandler();
					this.initParallelSessionHandler();

					this.isReconnecting = false;

					if (wasReconnect) {
						catchUpEventEnd();
						void this.getEventOverview().catch(
							(error: unknown): void => {
								this.logForegroundEventSyncError(error);
							},
						);
					}

					resolve();
				});

				this.socket.on(
					"disconnect",
					// @ts-expect-error - wrong types
					(reason: SocketIoDisconnectReason): void => {
						// eslint-disable-next-line no-console
						console.warn(
							...CONSOLE_COLOR,
							`socket disconnected: ${reason}`,
						);

						// Handle different disconnect reasons
						this.handleDisconnect(reason);
					},
				);

				// reconnect event handler
				this.socket.on("reconnect", (attemptNumber: number): void => {
					// eslint-disable-next-line no-console
					console.log(
						...CONSOLE_COLOR,
						`reconnected after ${attemptNumber} attempts`,
					);
					this.reconnectAttempts = 0;
				});

				this.socket.on(
					"reconnect_attempt",
					(attemptNumber: number): void => {
						// eslint-disable-next-line no-console
						console.log(
							...CONSOLE_COLOR,
							`reconnection attempt ${attemptNumber}`,
						);
					},
				);

				this.socket.on("reconnect_error", (error: Error): void => {
					console.error(
						...CONSOLE_COLOR,
						"reconnection error:",
						error,
					);
				});

				this.socket.on("reconnect_failed", (): void => {
					console.error(
						...CONSOLE_COLOR,
						"reconnection failed after max attempts",
					);
				});

				this.socket.on(
					"in_game_rewards_sync",
					(
						data: StatusResponse & InGameCompensationRewardsData,
					): void => {
						try {
							if (!data)
								throw new GameError(
									GameErrorType.BE_ERROR,
									"No data received",
								);

							parse(
								InGameCompensationRewardsLoadResponseValidationSchema,
								data,
							);

							if (!data.inGameRewards)
								throw new GameError(
									GameErrorType.BE_ERROR,
									"Failed to load in-game compensation rewards",
								);

							this.dispatchInGameRewards(data.inGameRewards);
						} catch (error: unknown) {
							console.error(
								...CONSOLE_COLOR,
								"Failed to load in-game compensation rewards",
								error,
							);
						}
					},
				);
			},
		);
	}

	private setupVisibilityHandlers(): void {
		// Handle visibility change events
		document.addEventListener(
			"visibilitychange",
			this.handleOnVisibilityChange,
		);

		// Handle connection issues
		window.addEventListener("online", this.handleOnline, { passive: true });

		// TODO: show offline popup (not coded yet)

		// Handle focus/blur events as backup
		window.addEventListener("focus", this.handleAppForeground);
		window.addEventListener("blur", this.handleAppBackground);

		// Handle pagehide/pageshow for iOS Safari
		window.addEventListener("pagehide", this.handleAppBackground);
		window.addEventListener("pageshow", this.handleAppForeground);

		// TODO: LK-1252
	}

	private handleAppBackground = (): void => {
		// eslint-disable-next-line no-console
		console.log(...CONSOLE_COLOR, "app went to background");
		this.isAppInBackground = true;

		// Don't immediately disconnect, but prepare for it. iOS will likely kill the connection anyway
	};

	private handleAppForeground = (): void => {
		// eslint-disable-next-line no-console
		console.log(...CONSOLE_COLOR, "app came to foreground");
		useRuntimeStore().setLastCameraChangeTimestamp(Date.now());
		this.isAppInBackground = false;

		catchUpEventEnd();

		// Check if socket is still connected when app comes back
		if (this.socket && !this.socket.connected) {
			// eslint-disable-next-line no-console
			console.log(...CONSOLE_COLOR, "reconnecting after background");
			this.attemptReconnect();
			return;
		}
	};

	private logForegroundEventSyncError(error: unknown): void {
		if (
			error instanceof GameError &&
			error.type === GameErrorType.NETWORK_DISCONNECT_DURING_REQUEST
		) {
			return;
		}
		console.error(
			...CONSOLE_COLOR,
			"event overview refresh after foreground failed",
			error,
		);
	}

	private handleDisconnect(reason: SocketIoDisconnectReason): void {
		if (
			this.socket &&
			(reason === SocketIoDisconnectReason.TRANSPORT_ERROR ||
				reason === SocketIoDisconnectReason.TRANSPORT_CLOSE ||
				reason === SocketIoDisconnectReason.PING_TIMEOUT)
		) {
			// Don't immediately reconnect if app is in background
			if (this.isAppInBackground) {
				// eslint-disable-next-line no-console
				console.log(
					...CONSOLE_COLOR,
					"app in background, deferring reconnect",
				);
				return;
			}

			this.attemptReconnect();
		}
	}

	private attemptReconnect(): void {
		if (
			this.isReconnecting ||
			this.reconnectAttempts >= this.maxReconnectAttempts
		) {
			return;
		}

		this.clearReconnectTimer();
		this.isReconnecting = true;
		this.reconnectAttempts++;

		// eslint-disable-next-line no-console
		console.log(
			...CONSOLE_COLOR,
			`attempting reconnect ${this.reconnectAttempts}/${this.maxReconnectAttempts}`,
		);

		// Use exponential backoff
		const delay = Math.min(
			Math.pow(2, this.reconnectAttempts - 1) * 1_000,
			30_000,
		);

		this.reconnectTimer = setTimeout((): void => {
			if (this.socket) {
				this.socket.connect();
			}
		}, delay);
	}

	private clearReconnectTimer(): void {
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = null;
		}
	}

	public disconnect(): void {
		if (!this.socket) return;

		this.clearReconnectTimer();

		// Remove the onvisibility change listeners
		document.removeEventListener(
			"visibilitychange",
			this.handleOnVisibilityChange,
		);

		// Remove event listeners
		window.removeEventListener("online", this.handleOnline);
		window.removeEventListener("focus", this.handleAppForeground);
		window.removeEventListener("blur", this.handleAppBackground);
		window.removeEventListener("pagehide", this.handleAppBackground);
		window.removeEventListener("pageshow", this.handleAppForeground);

		this.socket.removeAllListeners();
		this.socket.disconnect();
		this.socket = null;
	}

	private handleOnVisibilityChange = (): void => {
		if (document.visibilityState === "hidden") {
			this.handleAppBackground();
		} else if (document.visibilityState === "visible") {
			this.handleAppForeground();
		}
	};

	// Manually reconnect for UI.
	public async forceReconnect(): Promise<void> {
		this.disconnect();
		this.reconnectAttempts = 0;
		await this.connect();
	}

	public clear(deleteDeviceSecret: boolean = false): void {
		const deviceSecret =
			!deleteDeviceSecret && localStorage.getItem("deviceSecret");

		localStorage.clear();
		sessionStorage.clear();

		// https://stackoverflow.com/a/27374365
		document.cookie.split(";").forEach((name: string): void => {
			document.cookie = name
				.replace(/^ +/, "")
				.replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
		});

		if (deviceSecret) localStorage.setItem("deviceSecret", deviceSecret);
	}

	public reload(deleteToken: boolean = false): void {
		const searchParams = new URLSearchParams(window.location.search);
		if (!deleteToken && this.flutterToken)
			searchParams.append("token", this.flutterToken);

		const url = new URL(location.href);
		url.search = searchParams.toString();

		location.href = url.toString();
	}

	private initExceptionHandler(): void {
		if (!this.socket) throw new Error("Socket not found");

		this.socket.off("exception");
		this.socket.on("exception", (error: Error): void => {
			this.clear();

			throw new GameError(
				GameErrorType.BE_ERROR,
				`Socket exception occurred: ${error.message}`,
			);
		});
	}

	private initErrorHandler(): void {
		if (!this.socket) throw new Error("Socket not found");

		this.socket.off("error");
		this.socket.on("error", (error: Error): void => {
			this.clear();

			throw new GameError(
				GameErrorType.BE_ERROR,
				`Socket error occurred: ${error.message}`,
			);
		});
	}

	private initAuthErrorHandler(): void {
		if (!this.socket) throw new Error("Socket not found");

		this.socket.off("auth_error");
		this.socket.on("auth_error", (error: Error): void => {
			this.clear();
			console.error("Socket auth error occurred:", error);
			Communicator.getInstance().relogUser();
		});
	}

	private initReloadHandler(): void {
		if (!this.socket) throw new Error("Socket not found");

		this.socket.off("reload");
		this.socket.on("reload", (): void => this.reload());
	}

	private initParallelSessionHandler(): void {
		if (!this.socket) throw new Error("Socket not found");

		this.socket.off("parallel_session");
		this.socket.on("parallel_session", (error: Error): void => {
			throw new GameError(
				GameErrorType.PARALLEL_SESSION_ERROR,
				error.message,
			);
		});
	}

	public async emitStateRequest(): Promise<boolean> {
		return new Promise<boolean>(
			(resolve: CallableFunction, reject: CallableFunction): void => {
				if (!this.socket || !this.isSocketActive) {
					this.connect()
						.then((): void => {
							this.emitStateRequest()
								.then((maintenanceMode: boolean): void =>
									resolve(maintenanceMode),
								)
								.catch((error: unknown): void => reject(error));
						})
						.catch((error: unknown): void => reject(error));
					return;
				}

				const EMIT_NAME = "state";
				this.socket.timeout(REQUEST_TIMEOUT).emit(
					EMIT_NAME,
					(
						error: unknown,
						response: StatusResponse & {
							data: { maintenanceMode: boolean };
						},
					): void => {
						try {
							this.handleTimeoutError(
								"Socket error occurred while loading state",
								error,
							);

							if (!response)
								throw new GameError(
									GameErrorType.BE_ERROR,
									"No data received",
								);

							parse(StateResponseValidationSchema, response);

							if (!response.status)
								throw new GameError(
									GameErrorType.BE_ERROR,
									"Failed to load state",
								);

							const maintenanceMode =
								response.data.maintenanceMode;
							useRuntimeStore().setIsMaintenanceMode(
								maintenanceMode,
							);
							resolve(maintenanceMode);
						} catch (error: unknown) {
							this.handleError(EMIT_NAME, error, reject);
						}
					},
				);
			},
		);
	}

	public async emitLoadRequest(
		requestPayload?: LoadRequestPayload,
	): Promise<MapData & ClaimableRewardsData> {
		return new Promise<MapData & ClaimableRewardsData>(
			(resolve: CallableFunction, reject: CallableFunction): void => {
				if (!this.socket) {
					this.connect()
						.then((): void => {
							this.emitLoadRequest(requestPayload)
								.then(
									(
										data: MapData & ClaimableRewardsData,
									): void => resolve(data),
								)
								.catch((error: unknown): void => reject(error));
						})
						.catch((error: unknown): void => reject(error));
					return;
				}

				const requestedStores = requestPayload?.stores ?? [
					...ALL_STORES,
				];

				const EMIT_NAME = "load";
				this.socket
					.timeout(REQUEST_TIMEOUT)
					.emit(
						EMIT_NAME,
						{ stores: requestedStores },
						(error: unknown, response: LoadResponse): void => {
							try {
								this.handleTimeoutError(
									`Socket error occurred while emitting ${EMIT_NAME} request`,
									error,
								);

								// Silently fail.
								// if (!this.isSocketActive || this.isReconnecting) return;

								if (!response)
									throw new GameError(
										GameErrorType.BE_ERROR,
										"No data received",
									);

								parse(LoadResponseValidationSchema, response);

								if (!response.status)
									throw new GameError(
										GameErrorType.BE_ERROR,
										"Failed to load data",
									);

								if (response.data.maintenanceMode) {
									useRuntimeStore().setIsMaintenanceMode(
										true,
									);
									// Musime ignorovat pokracovanie
									resolve({
										tiles: [],
										claimableRewards: [],
									});
									return;
								} else {
									useRuntimeStore().setIsMaintenanceMode(
										false,
									);
								}

								if (response.data.user?.last_tick_hash) {
									const lastSavedTickLocaly =
										localStorage.getItem("last_tick_hash");

									if (
										lastSavedTickLocaly !==
										response.data.user.last_tick_hash
									) {
										TileRepository.deleteDatabase();

										delete response.data.user
											.last_tick_hash;
									}
								}

								const getStore = (
									key: keyof typeof response.data,
								): ReturnType<
									| typeof useMapExpansionStore
									| typeof useResourcesStore
									| typeof useBuildingsStorageStore
									| typeof useSettlementsStore
									| typeof useQuestStore
									| typeof useQuickToolStore
									| typeof useOfferStore
									| typeof useSettingsStore
									| typeof useUpgradesStore
									| typeof useUserStore
									| typeof useShopStore
									| typeof useNewsStore
									| typeof useChestStore
									| typeof useEventStore
									| typeof useAchievementsStore
									| typeof useSiegeSystemStore
								> | null => {
									switch (key) {
										case "mapExpansion":
											return useMapExpansionStore();
										case "resources":
											return useResourcesStore();
										case "buildingsStorage":
											return useBuildingsStorageStore();
										case "settlements":
											return useSettlementsStore();
										case "quests":
											return useQuestStore();
										case "offer":
											return useOfferStore();
										case "settings":
											return useSettingsStore();
										case "upgrades":
											return useUpgradesStore();
										case "user":
											return useUserStore();
										case "packs":
											return useShopStore();
										case "flashNews":
											return useNewsStore();
										case "chest":
											return useChestStore();
										case "eventOverview":
											return useEventStore();
										case "achievements":
											return useAchievementsStore();
										case "siegeSystem":
											return useSiegeSystemStore();
										default:
											return null;
									}
								};

								const patchLoadStore = (
									key: keyof (typeof response)["data"],
								): void => {
									if (key === "flashNews") {
										if (response.data.flashNews) {
											// eslint-disable-next-line no-console
											console.log(
												"Patching flash news store state",
												response.data.flashNews,
											);

											useNewsStore().$setState(
												response.data.flashNews,
											);
										}
										return;
									}

									if (!response.data[key]) return;

									const $store = getStore(key);
									if (!$store) return;

									// eslint-disable-next-line no-console
									console.log(
										`Patching ${key} store state`,
										response.data[key],
									);

									// @ts-expect-error - TS2345
									$store.$setState(response.data[key]);
								};

								for (const key of LOAD_STORE_KEYS_BEFORE_EVENT_OVERVIEW) {
									patchLoadStore(key);
								}

								if (response.data.buildingsStorage) {
									//ak je aktívny event, ktorý môže byť iba jeden aktívny a zároveň má hráč LVL nato
									const levelUnlock =
										response.data.eventOverview?.event
											?.attributes?.level_unlock ?? 0;
									if (
										response.data.eventOverview?.event
											?.active &&
										levelUnlock <=
											useUserStore().playerLevel
									) {
										//ak používateľ ešte neprevzal budovy v úvode eventu
										if (
											!response.data.eventOverview
												?.initBuildingsClaimed
										) {
											// 1. Vytiahneme si grants, ak by chýbali, dosadíme prázdne pole
											const grants =
												response.data.eventOverview
													?.starterBuildingGrants ??
												[];

											// Pomocná množina pre budovy, ktoré reálne patria do aktuálneho eventu
											const grantedBuildingsInThisEvent =
												new Set<FrameName>();

											// 2. Preiterujeme všetky odmeny z eventu
											for (const grant of grants) {
												// Preskočíme nevalidné záznamy, ak by nejaké boli
												if (!grant.frameName) continue;
												const frameName: FrameName =
													grant.frameName as FrameName;
												// Preistotu pretypujeme na číslo (pripravené na bežné number aj MongoDB formáty)
												const amountToAdd: number =
													typeof grant.amount ===
													"number"
														? grant.amount
														: +grant.amount;
												response.data.buildingsStorage[
													frameName
												] = amountToAdd;

												// Zaznačíme si, že táto budova patrí k aktuálne prebiehajúcemu eventu
												grantedBuildingsInThisEvent.add(
													frameName,
												);
											}

											// 3. Vynulujeme všetky ostatné eventové budovy, ktoré neboli súčasťou aktuálnych grants
											for (const eventBuilding of tilerepoRemovableEventBuildingsFrameNames) {
												if (
													!grantedBuildingsInThisEvent.has(
														eventBuilding,
													)
												) {
													response.data.buildingsStorage[
														eventBuilding
													] = 0;
												}
											}

											response.data.eventOverview.initBuildingsClaimed = true;
										}
									}

									useBuildingsStorageStore().$setState(
										response.data.buildingsStorage,
									);
								}

								if (response.data.eventOverview) {
									useEventStore().$setState(
										response.data.eventOverview,
									);
									if (
										typeof response.data.eventOverview
											?.eventGeneration === "number"
									) {
										useUserStore().setAppliedEventGeneration(
											response.data.eventOverview
												.eventGeneration,
										);
									}
								}

								const stripEventResourcesOnLoad =
									shouldStripEventResourcesOnLoad({
										eventId: useEventStore().id,
										endDate: useEventStore().endDate,
										serverEventActive:
											useEventStore().serverEventActive,
									});

								if (response.data.resources) {
									if (stripEventResourcesOnLoad) {
										zeroEventiumInResourcePatch(
											response.data.resources,
										);
									}
									patchLoadStore("resources");
								}

								if (stripEventResourcesOnLoad) {
									for (const upgradeId of eventUpgrades.keys()) {
										useUpgradesStore().setUpgradeLevelById(
											upgradeId,
											0,
										);
									}
								}

								catchUpEventEnd();

								resolve({
									tiles: response.data.map?.tiles,
									claimableRewards:
										response.data.claimableRewards,
								});
							} catch (error: unknown) {
								this.handleError(EMIT_NAME, error, reject);
							}
						},
					);
			},
		);
	}

	public async emitSaveRequest(
		requestPayload?: SaveRequestPayload,
	): Promise<void> {
		return new Promise<void>(
			(resolve: CallableFunction, reject: CallableFunction): void => {
				if (FLAG_REVEAL_MAP || !GAME_TICKER_ENABLED) return resolve();

				if (!this.socket) {
					this.connect()
						.then((): void => {
							this.emitSaveRequest(requestPayload)
								.then((): void => resolve())
								.catch((error: unknown): void => reject(error));
						})
						.catch((error: unknown): void => reject(error));
					return;
				}

				const stores = requestPayload?.stores ?? [...ALL_STORES];
				const claimedRewards = requestPayload?.claimedRewards ?? [];

				// helper to avoid repeating includes()
				const shouldIncludeStore = (store: EmitStores): boolean =>
					stores.includes(store);

				const payload = {
					appliedEventGeneration: appliedEventGenerationForSave(),
					claimedRewards,
					eventMilestones: requestPayload?.eventMilestones,
					eventGrandPrizeState: requestPayload?.eventGrandPrizeState,
					eventUserData: useEventStore()?.isActive
						? {
								event_starter_buildings_granted:
									useEventStore().initBuildingsClaimed,
								event_intro_dismissed:
									useEventStore().introDismissed,
								event_starter_map_cleaned:
									useEventStore().starterMapCleaned,
							}
						: undefined,
					map: shouldIncludeStore(EmitStores.MAP)
						? useMapActions().getPayload()
						: undefined,

					mapExpansion: shouldIncludeStore(EmitStores.MAP_EXPANSION)
						? useMapExpansionStore().$getPayload()
						: undefined,

					resources: shouldIncludeStore(EmitStores.RESOURCES)
						? useResourcesStore().$getPayload()
						: undefined,

					buildingsStorage: shouldIncludeStore(
						EmitStores.BUILDINGS_STORAGE,
					)
						? useBuildingsStorageStore().$getPayload()
						: undefined,

					settlements: shouldIncludeStore(EmitStores.SETTLEMENTS)
						? useSettlementsStore().$getPayload()
						: undefined,

					quests: shouldIncludeStore(EmitStores.QUESTS)
						? useQuestStore().$getPayload()
						: undefined,

					offer: shouldIncludeStore(EmitStores.OFFER)
						? useOfferStore().$getPayload()
						: undefined,

					settings: shouldIncludeStore(EmitStores.SETTINGS)
						? useSettingsStore().$getPayload()
						: undefined,

					upgrades: shouldIncludeStore(EmitStores.UPGRADES)
						? useUpgradesStore().$getPayload()
						: undefined,

					user: shouldIncludeStore(EmitStores.USER)
						? useUserStore().$getPayload()
						: undefined,

					chest: shouldIncludeStore(EmitStores.CHEST)
						? useChestStore().$getPayload()
						: undefined,

					achievements: shouldIncludeStore(EmitStores.ACHIEVEMENTS)
						? useAchievementsStore().$getPayload()
						: undefined,

					siegeSystem: shouldIncludeStore(EmitStores.SIEGE_SYSTEM)
						? useSiegeSystemStore().$getPayload()
						: undefined,

					eventMapCollectables: eventMapCollectablesForSave(
						shouldIncludeStore(EmitStores.EVENT_MAP_COLLECTABLES),
					),
				};

				const bytes = new Uint8Array(32);
				crypto.getRandomValues(bytes);
				const hash = Array.from(bytes, (b: number): string =>
					b.toString(16).padStart(2, "0"),
				).join("");

				if (payload.user?.state) {
					payload.user.state.last_tick_hash = hash;
				}

				const EMIT_NAME = "save";
				this.socket
					.timeout(REQUEST_TIMEOUT)
					.emit(
						EMIT_NAME,
						payload,
						(error: unknown, response: SaveResponse): void => {
							try {
								this.handleTimeoutError(
									`Socket error occurred while emitting ${EMIT_NAME} request`,
									error,
								);

								// Silently fail.
								// if (!this.isSocketActive || this.isReconnecting) return;

								if (!response)
									throw new GameError(
										GameErrorType.BE_ERROR,
										"No data received",
									);

								parse(SaveResponseValidationSchema, response);

								if (!response.status)
									throw new GameError(
										GameErrorType.BE_ERROR,
										"Failed to save data",
									);

								// Zabitie dopytu lebo maintenance
								if (response.data?.maintenanceMode) {
									useRuntimeStore().setIsMaintenanceMode(
										true,
									);
									resolve();
									return;
								} else {
									useRuntimeStore().setIsMaintenanceMode(
										false,
									);
								}

								if (payload.map !== undefined)
									useMapActions().resetPayload();
								if (payload.mapExpansion !== undefined)
									useMapExpansionStore().$resetPayload();
								if (payload.resources !== undefined)
									useResourcesStore().$resetPayload();
								if (payload.buildingsStorage !== undefined)
									useBuildingsStorageStore().$resetPayload();
								if (payload.settlements !== undefined)
									useSettlementsStore().$resetPayload();
								if (payload.quests !== undefined)
									useQuestStore().$resetPayload();
								if (payload.offer !== undefined)
									useOfferStore().$resetPayload();
								if (payload.settings !== undefined)
									useSettingsStore().$resetPayload();
								if (payload.upgrades !== undefined)
									useUpgradesStore().$resetPayload();
								if (payload.user !== undefined)
									useUserStore().$resetPayload();
								if (payload.chest !== undefined)
									useChestStore().$resetPayload();
								if (payload.achievements !== undefined)
									useAchievementsStore().$resetPayload();
								if (payload.siegeSystem !== undefined)
									useSiegeSystemStore().$resetPayload();

								if (payload.user) {
									localStorage.setItem(
										"last_tick_hash",
										hash,
									);
								}

								const eventStore = useEventStore();

								if (response?.data?.event) {
									eventStore.$setState({
										event: response.data.event,
									});
								}

								resolve();
							} catch (error: unknown) {
								this.handleError(EMIT_NAME, error, reject);
							}
						},
					);
			},
		);
	}

	public async initLanguageListHandler(): Promise<LanguesList> {
		return new Promise<LanguesList>(
			(resolve: CallableFunction, reject: CallableFunction): void => {
				if (!this.socket) return reject("Socket not found");

				const EMIT_NAME = "list_languages";

				this.socket
					.timeout(REQUEST_TIMEOUT)
					.emit(
						EMIT_NAME,
						(
							error: unknown,
							data: StatusResponse & LanguesList,
						): void => {
							try {
								this.handleTimeoutError(
									"Socket error occurred while loading language list",
									error,
								);

								if (!data)
									throw new GameError(
										GameErrorType.BE_ERROR,
										"No data received",
									);

								parse(LanguesListValidationSchema, data);

								if (!data.languages)
									throw new GameError(
										GameErrorType.BE_ERROR,
										"Failed to load language list",
									);

								resolve(data);
							} catch (error: unknown) {
								this.handleError(EMIT_NAME, error, reject);
							}
						},
					);
			},
		);
	}

	public async fetchTranslations(
		requestData: TranslationsFetchRequest,
	): Promise<TranslationsRefetchedData | TranslationsNotChangedData> {
		return new Promise<
			TranslationsRefetchedData | TranslationsNotChangedData
		>((resolve: CallableFunction, reject: CallableFunction): void => {
			if (!this.socket) return reject("Socket not found");

			const EMIT_NAME = "fetch_translations";

			this.socket
				.timeout(REQUEST_TIMEOUT)
				.emit(
					EMIT_NAME,
					requestData,
					(error: unknown, data: TranslationsRefetchedData): void => {
						try {
							this.handleTimeoutError(
								"Socket error occurred while fetching translations",
								error,
							);

							// TODO: Preco to je zakomentovane ?!
							// parse(TranslationsRefetchedValidationSchema, data);

							resolve(data);
						} catch (error: unknown) {
							this.handleError(EMIT_NAME, error, reject);
						}
					},
				);
		});
	}

	public async deleteAccount(): Promise<StatusResponse> {
		return new Promise<StatusResponse>(
			(resolve: CallableFunction, reject: CallableFunction): void => {
				if (!this.socket) return reject("Socket not found");

				const EMIT_NAME = "delete_user";

				this.socket
					.timeout(REQUEST_TIMEOUT)
					.emit(
						EMIT_NAME,
						(error: unknown, data: StatusResponse): void => {
							try {
								this.handleTimeoutError(
									"Socket error occurred while deleting account",
									error,
								);

								if (!data)
									throw new GameError(
										GameErrorType.BE_ERROR,
										"No data received",
									);

								parse(DeleteUserResultValidationSchema, data);

								if (!data.status)
									throw new GameError(
										GameErrorType.BE_ERROR,
										"Failed to delete account",
									);

								this.clear(true);
								if (IS_DEV) this.reload(true);
								else Communicator.getInstance().resetGame();

								resolve(data);
							} catch (error: unknown) {
								this.handleError(EMIT_NAME, error, reject);
							}
						},
					);
			},
		);
	}

	public async getUserById(userId: string): Promise<UserData> {
		return new Promise<UserData>(
			(resolve: CallableFunction, reject: CallableFunction): void => {
				if (!this.socket) return reject("Socket not found");

				const EMIT_NAME = "get_user_by_id";

				this.socket
					.timeout(REQUEST_TIMEOUT)
					.emit(
						EMIT_NAME,
						{ userId },
						(
							error: unknown,
							data: { user: UserData } & StatusResponse,
						): void => {
							try {
								this.handleTimeoutError(
									"Socket error occurred while getting user by id",
									error,
								);

								if (!data)
									throw new GameError(
										GameErrorType.BE_ERROR,
										"No data received",
									);

								parse(GetUserByIdResultValidationSchema, data);

								if (!data.status)
									throw new GameError(
										GameErrorType.BE_ERROR,
										"Failed to get user by id",
									);

								resolve(data.user);
							} catch (error: unknown) {
								this.handleError(EMIT_NAME, error, reject);
							}
						},
					);
			},
		);
	}

	public async changeUserName(name: string): Promise<string> {
		return new Promise<string>(
			(resolve: CallableFunction, reject: CallableFunction): void => {
				if (!this.socket) return reject("Socket not found");

				const EMIT_NAME = "change_user_name";

				this.socket
					.timeout(REQUEST_TIMEOUT)
					.emit(
						EMIT_NAME,
						{ name },
						(error: unknown, data: { reason: string }): void => {
							try {
								this.handleTimeoutError(
									"Socket error occurred while changing user name",
									error,
								);

								if (!data)
									throw new GameError(
										GameErrorType.BE_ERROR,
										"No data received",
									);

								parse(ChangeNameResultValidationSchema, data);

								resolve(data.reason);
							} catch (error: unknown) {
								this.handleError(EMIT_NAME, error, reject);
							}
						},
					);
			},
		);
	}

	public async getLeaderboard(): Promise<LeaderboardResult> {
		return new Promise<LeaderboardResult>(
			(resolve: CallableFunction, reject: CallableFunction): void => {
				if (!this.socket) return reject("Socket not found");

				const EMIT_NAME = "get_leaderboard";

				this.socket.timeout(REQUEST_TIMEOUT).emit(
					EMIT_NAME,
					(
						error: unknown,
						data: {
							leaderboard: LeaderboardResult;
						} & StatusResponse,
					): void => {
						try {
							this.handleTimeoutError(
								"Socket error occurred while getting leaderboard",
								error,
							);

							if (!data)
								throw new GameError(
									GameErrorType.BE_ERROR,
									"No data received",
								);

							parse(GetScoreboardResultValidationSchema, data);

							if (!data.status)
								throw new GameError(
									GameErrorType.BE_ERROR,
									"Failed to get leaderboard",
								);

							resolve(data.leaderboard);
						} catch (error: unknown) {
							this.handleError(EMIT_NAME, error, reject);
						}
					},
				);
			},
		);
	}

	public async getEventOverview(): Promise<EventOverviewResult> {
		if (!this.socket)
			throw new GameError(GameErrorType.BE_ERROR, "Socket not found");
		return fetchEventOverviewTransport(
			this.socket,
			this.eventTransportDeps(),
		) as Promise<EventOverviewResult>;
	}

	public async emitEventPassPurchased(payload: {
		status: boolean;
	}): Promise<void> {
		if (!this.socket)
			throw new GameError(GameErrorType.BE_ERROR, "Socket not found");
		return emitEventPassPurchasedTransport(
			this.socket,
			this.eventTransportDeps(),
			payload,
		);
	}

	public async getClaimableRewards(): Promise<ClaimableRewardsData> {
		return new Promise<ClaimableRewardsData>(
			(resolve: CallableFunction, reject: CallableFunction): void => {
				if (!this.socket) return reject("Socket not found");

				const EMIT_NAME = "get_claimable_rewards";

				this.socket
					.timeout(REQUEST_TIMEOUT)
					.emit(
						EMIT_NAME,
						(
							error: unknown,
							data: StatusResponse & ClaimableRewardsData,
						): void => {
							try {
								this.handleTimeoutError(
									"Socket error occurred while loading claimable rewards",
									error,
								);

								if (!data)
									throw new GameError(
										GameErrorType.BE_ERROR,
										"No data received",
									);

								parse(
									ClaimableRewardsLoadResponseValidationSchema,
									data,
								);

								if (!data.claimableRewards)
									throw new GameError(
										GameErrorType.BE_ERROR,
										"Failed to load claimable rewards",
									);

								resolve(data);
							} catch (error: unknown) {
								this.handleError(EMIT_NAME, error, reject);
							}
						},
					);
			},
		);
	}

	public async getInGameCompensationRewards(): Promise<InGameCompensationRewardsData> {
		return new Promise<InGameCompensationRewardsData>(
			(resolve: CallableFunction, reject: CallableFunction): void => {
				if (!this.socket) return reject("Socket not found");

				const EMIT_NAME = "get_in_game_rewards";

				this.socket
					.timeout(REQUEST_TIMEOUT)
					.emit(
						EMIT_NAME,
						(
							error: unknown,
							data: StatusResponse &
								InGameCompensationRewardsData,
						): void => {
							try {
								this.handleTimeoutError(
									"Socket error occurred while loading in-game compensation rewards",
									error,
								);

								if (!data)
									throw new GameError(
										GameErrorType.BE_ERROR,
										"No data received",
									);

								parse(
									InGameCompensationRewardsLoadResponseValidationSchema,
									data,
								);

								if (!data.inGameRewards)
									throw new GameError(
										GameErrorType.BE_ERROR,
										"Failed to load in-game compensation rewards",
									);

								this.dispatchInGameRewards(data.inGameRewards);

								resolve(data);
							} catch (error: unknown) {
								this.handleError(EMIT_NAME, error, reject);
							}
						},
					);
			},
		);
	}

	public async emitClaimCompensationRewards(payload: {
		ids: string[];
	}): Promise<void> {
		return new Promise<void>(
			(resolve: CallableFunction, reject: CallableFunction): void => {
				if (!this.socket) return reject("Socket not found");

				const EMIT_NAME = "claim_in_game_rewards";

				this.socket
					.timeout(REQUEST_TIMEOUT)
					.emit(
						EMIT_NAME,
						payload,
						(error: unknown, data: StatusResponse): void => {
							try {
								this.handleTimeoutError(
									"Socket error occurred while sending claim in-game compensation rewards event",
									error,
								);

								if (!data.status)
									throw new GameError(
										GameErrorType.BE_ERROR,
										"Failed to claim in-game compensation rewards",
									);

								resolve(data);
							} catch (error: unknown) {
								this.handleError(EMIT_NAME, error, reject);
							}
						},
					);
			},
		);
	}

	public async emitOfferAnalyticsSave(
		payload: OfferAnalyticsData,
	): Promise<void> {
		return new Promise<void>(
			(resolve: CallableFunction, reject: CallableFunction): void => {
				if (!this.socket) return reject("Socket not found");

				const EMIT_NAME = "offer_analytics";

				if (!payload)
					return reject("Resources store payload not found");

				// alert("analytics event: " + JSON.stringify(payload, null, 2));

				this.socket
					.timeout(REQUEST_TIMEOUT)
					.emit(
						EMIT_NAME,
						payload,
						(error: unknown, data: StatusResponse): void => {
							try {
								this.handleTimeoutError(
									"Socket error occurred while sending offer analytics event",
									error,
								);

								if (!data)
									throw new GameError(
										GameErrorType.BE_ERROR,
										"No data received",
									);

								parse(
									OfferAnalyticsSaveResultValidationSchema,
									data,
								);

								if (!data.status)
									throw new GameError(
										GameErrorType.BE_ERROR,
										"Failed to offer analytics save",
									);

								resolve();
							} catch (error: unknown) {
								this.handleError(EMIT_NAME, error, reject);
							}
						},
					);
			},
		);
	}

	public async emitAdAnalyticsSave(payload: AdAnalyticsData): Promise<void> {
		return new Promise<void>(
			(resolve: CallableFunction, reject: CallableFunction): void => {
				if (!this.socket) return reject("Socket not found");

				const EMIT_NAME = "ad_analytics";

				if (!payload)
					return reject("Resources store payload not found");

				// alert("analytics event: " + JSON.stringify(payload, null, 2));

				this.socket
					.timeout(REQUEST_TIMEOUT)
					.emit(
						EMIT_NAME,
						payload,
						(error: unknown, data: StatusResponse): void => {
							try {
								this.handleTimeoutError(
									"Socket error occurred while sending ad analytics event",
									error,
								);

								parse(
									AdAnalyticsSaveResultValidationSchema,
									data,
								);

								if (!data.status)
									throw new GameError(
										GameErrorType.BE_ERROR,
										"Failed to ad analytics save",
									);

								resolve();
							} catch (error: unknown) {
								this.handleError(EMIT_NAME, error, reject);
							}
						},
					);
			},
		);
	}
}

export default SocketService;
